import {
    Cart,
    CartItem,
    SellingPlanAllocation,
    SellingPlanFixedAmountAdjustment,
    SellingPlanFixedPriceAdjustment,
    SellingPlanGroup,
    SellingPlanPercentageAdjustment,
    SellingPlanPriceAdjustment,
} from '../types'
import { ViddyLPContext } from '../context/viddyLPContext'
import { useContext } from 'react'
import { ApiClientSettings } from '@storefronts/common-types'
import { hasAppliedDiscountCode } from './discountAllocations'

type SubscriptionSetting = Pick<
    ApiClientSettings,
    'sellingPlanGroupIds' | 'subscriptionButtonText' | 'subscriptionAppId'
>

// Hook to simplify fetching the subscription Options.
export function useSubscriptionOptions(): SubscriptionSetting {
    const { landingPage } = useContext(ViddyLPContext)

    const { sellingPlanGroupIds, subscriptionButtonText, subscriptionAppId } =
        landingPage?.settings ?? {
            sellingPlanGroupIds: [],
            subscriptionButtonText: null,
            subscriptionAppId: null,
        }
    return {
        sellingPlanGroupIds,
        subscriptionButtonText: subscriptionButtonText ?? null,
        subscriptionAppId: subscriptionAppId ?? null,
    }
}

function logSubscriptionIssue(key: string, message: string) {
    // Trying to limit flooding to onces/session for each kind?
    if (
        typeof sessionStorage !== 'undefined' &&
        sessionStorage &&
        !sessionStorage.getItem(key)
    ) {
        sessionStorage.setItem(key, message)
        console.warn(message)
    }
}

export function getValidSellingPlanGroups(
    cartItem: CartItem,
    subscriptionOptions?: SubscriptionSetting,
) {
    // Quantity of 0 means item is not available.
    // TBD: Limit this only when cartItem.variant.available is false?
    if (cartItem.quantity === 0) {
        return []
    }

    const nonEmptySellingPlanGroups = (cartItem.sellingPlanGroups ?? []).filter(
        (group) => !!group.sellingPlansIds?.length,
    )

    // Fetch the selling plan IDs for the cart items's available selling plan allocations.
    const validSellingPlanIds = [
        ...new Set(
            getSubscriptionSellingPlanAllocations(cartItem).map(
                (allocation) => allocation.sellingPlan.id,
            ),
        ),
    ]

    // Filter out selling plan groups that don't manage any of the valid selling plan IDs,
    // and are not using the configured subscription app id (if configured)
    const validSellingPlanGroups = nonEmptySellingPlanGroups.filter(
        (group) =>
            validSellingPlanIds.some((id) =>
                group.sellingPlansIds.includes(id),
            ) &&
            (!subscriptionOptions?.subscriptionAppId ||
                subscriptionOptions.subscriptionAppId === group.appName) &&
            (!subscriptionOptions?.sellingPlanGroupIds?.length ||
                (group.id &&
                    subscriptionOptions.sellingPlanGroupIds.includes(
                        group.id.replace('gid://shopify/SellingPlanGroup/', ''),
                    ))),
    )

    const areSellingPlanGroupsFromSameApp = validSellingPlanGroups.every(
        (group) => group.appName === validSellingPlanGroups[0].appName,
    )

    if (!areSellingPlanGroupsFromSameApp) {
        logSubscriptionIssue(
            'MISMATCHED_APPS',
            `Disabling subscriptions as variant ${cartItem.variant.shopifyId} has multiple active selling plan groups with different appName ${[...new Set(validSellingPlanGroups.map((value) => value.appName))].join(', ')}`,
        )
        return []
    }

    return validSellingPlanGroups
}

function getSubscriptionSellingPlanAllocations(cartItem: CartItem) {
    // Assume selling plan allocations that have recurring deliveries are subscriptions
    return (
        cartItem.availableSellingPlanAllocations?.filter(
            ({ sellingPlan }) =>
                sellingPlan.recurringDeliveries &&
                // NOTE: Quick hack for Clean Cause which seems to mark
                // selling plans to hide by naming it with "{{H}}"
                // TBD if we want to introduce more configuration options
                // in the config front-end to capture these cases if more come up.
                !sellingPlan.name.startsWith('{{H}}'),
        ) ?? []
    )
}

export function getValidSellingPlanAllocations(
    cartItem: CartItem,
    subscriptionOptions?: SubscriptionSetting,
) {
    // Get all of the selling plan groups that are available
    const sellingPlanGroups = getValidSellingPlanGroups(
        cartItem,
        subscriptionOptions,
    )
    // Get all the selling plan IDs accross all SellingPlans
    const allSellingPlanIds = sellingPlanGroups
        .map(({ sellingPlansIds }) => sellingPlansIds)
        .flat()
    // Get all Subscriptions from the cart's Selling Plan Allocations
    const subscriptionAllocations =
        getSubscriptionSellingPlanAllocations(cartItem)
    // Return all Allocations from all from all of the SellingPlans.
    return subscriptionAllocations.filter(({ sellingPlan }) =>
        allSellingPlanIds.includes(sellingPlan.id),
    )
}

// analyze and log any suspicious situations.
function analyzeSellingPlans(
    cartItem: CartItem,
    groups: SellingPlanGroup[],
    allocations: SellingPlanAllocation[],
) {
    // Test for duplicate names.
    // duplicate options texts?  (we can compare "2 weeks" to "2 weeks", but it gets tricker if it's "2 weeks", "14 days", "bi-monthly").
    const names = allocations.map(({ sellingPlan }) =>
        sellingPlan.name.toLowerCase().trim(),
    )
    const uniqueNames = new Set(names)
    if (names.length !== uniqueNames.size) {
        logSubscriptionIssue(
            'DUPLICATE_OPTIONS',
            `Variant ${cartItem.variant.shopifyId} has duplicate selling plans.`,
        )
        return
    }
    // We are expecting 1 group with multiple selling plans/allocations
    // or 1 group for each selling plan allocations.
    // A mismatch means that there might be duplicates.
    if (groups.length > 1 && groups.length !== allocations.length) {
        logSubscriptionIssue(
            'MULTIPLE_ACTIVE_GROUPS',
            `Variant ${cartItem.variant.shopifyId} has a suspicious number of active of selling plan groups (${groups.length} to ${allocations.length}). Confirm there are no duplicates/redundant options.`,
        )
        return
    }

    // test for large number of options? >6?
    if (allocations.length > 6) {
        logSubscriptionIssue(
            `MANY_OPTIONS_${allocations.length}`,
            `Variant ${cartItem.variant.shopifyId} has a suspicious number of active selling plans (${allocations.length}). Confirm that there are no duplicates.`,
        )
    }
}

export function getValidSubscriptionOptions(
    cartItem: CartItem,
    subscriptionOptions?: SubscriptionSetting,
) {
    const validSellingPlanGroups = getValidSellingPlanGroups(
        cartItem,
        subscriptionOptions,
    )
    // if there are no valid selling plan groups, then there are no
    // valid subscription options
    if (validSellingPlanGroups.length === 0) {
        return []
    }

    const validSellingPlanAllocations = getValidSellingPlanAllocations(
        cartItem,
        subscriptionOptions,
    )
    analyzeSellingPlans(
        cartItem,
        validSellingPlanGroups,
        validSellingPlanAllocations,
    )
    return validSellingPlanAllocations
}

export function hasSubscriptionOptions(
    cartItem: CartItem,
    subscriptionOptions?: SubscriptionSetting,
) {
    return getValidSellingPlanGroups(cartItem, subscriptionOptions).length > 0
}

/**
 * When the brand hides cart subscription options, items already on a plan
 * (picked on a product page) keep their selector so the shopper can still
 * see or drop the subscription.
 */
export function shouldShowCartSubscription(
    cartItem: CartItem,
    subscriptionOptions: SubscriptionSetting,
    displayCartSubscriptionOptions: boolean,
) {
    if (!hasSubscriptionOptions(cartItem, subscriptionOptions)) {
        return false
    }
    return (
        displayCartSubscriptionOptions ||
        !!getCurrentSubscriptionOption(cartItem, subscriptionOptions)
    )
}

export function getSubscriptionOptionById(
    cartItem: CartItem,
    id?: string | null,
    subscriptionOptions?: SubscriptionSetting,
) {
    if (!id) {
        return undefined
    }
    return getValidSellingPlanAllocations(cartItem, subscriptionOptions).find(
        (allocation) => allocation.sellingPlan.id === id,
    )
}

export function getCurrentSubscriptionOption(
    cartItem: CartItem,
    subscriptionOptions?: SubscriptionSetting,
) {
    return getSubscriptionOptionById(
        cartItem,
        cartItem.sellingPlanId,
        subscriptionOptions,
    )
}

export function getSubscriptionText(
    cartItem: CartItem,
    subscriptionSettings?: SubscriptionSetting,
) {
    const subscriptionOptions = getValidSubscriptionOptions(
        cartItem,
        subscriptionSettings,
    )
    const priceAdjustments = subscriptionOptions
        .map((option) => {
            const returnValue = option.sellingPlan.priceAdjustments.map(
                ({ adjustmentValue, orderCount }, index) => {
                    if (isFixedPriceAdjustment(adjustmentValue)) {
                        // if Fixed price adjustment, convert to a fixed amount adjustment
                        // by subtracting the new price from the original price.
                        // Doing this at this point since we need the sellingPlanAllocatiion's
                        // priceAdjustments.compareAtPrice to figure out the original price.
                        const originalPrices = [
                            cartItem.variant.price.price,
                            option.priceAdjustments[
                                Math.min(
                                    index,
                                    option.priceAdjustments.length - 1,
                                )
                            ]?.compareAtPrice,
                        ].filter(Boolean) as number[]
                        if (originalPrices.length) {
                            const originalPrice = Math.max(...originalPrices)
                            return {
                                adjustmentValue: {
                                    amount:
                                        originalPrice -
                                        (
                                            adjustmentValue as SellingPlanFixedPriceAdjustment
                                        ).price,
                                },
                                orderCount,
                            }
                        }
                    }
                    return {
                        adjustmentValue,
                        orderCount,
                    }
                },
            )
            return returnValue
        })
        .flat()
        .filter(({ orderCount }) => {
            // orderCount undefiend means price adjustment applies to all orders.
            // orderCount of 0 means price adjustment doesn't apply to any orders.
            // orderCount > 0 means price adjustment applies to a specific number of orders.
            return orderCount !== 0
        })
        .map(({ adjustmentValue }) => adjustmentValue)

    const allPercentageAdjustments = priceAdjustments.every((adjustment) =>
        isPercentageAdjustment(adjustment),
    )

    const discountedValues = priceAdjustments.map((adjustmentValue) => {
        if (isPercentageAdjustment(adjustmentValue)) {
            return (adjustmentValue as SellingPlanPercentageAdjustment)
                .percentage
        }
        if (isFixedAmountAdjustment(adjustmentValue)) {
            return (adjustmentValue as SellingPlanFixedAmountAdjustment).amount
        }
        // if we're here, then the fixedPriceAdjustment couldn't be handled
        // earlier.
        return 0
    })

    const areAllValuesDiscounted = discountedValues.every(
        // A value >  0 means the item has a discount
        (value) => value && value > 0,
    )

    if (!areAllValuesDiscounted || !subscriptionOptions.length) {
        return 'Auto-Replenishment'
    }

    if (allPercentageAdjustments && !hasAppliedDiscountCode(cartItem)) {
        const allPercentageAdjustmentsAreTheSame = discountedValues.every(
            (value) => value === discountedValues[0],
        )
        const allPercentageAdjustmentsAreWholeIntegers = discountedValues.every(
            (value) => value === Math.round(value),
        )
        const hasValidPercentage =
            discountedValues.length > 0 &&
            typeof discountedValues[0] === 'number'
        if (
            hasValidPercentage &&
            allPercentageAdjustmentsAreTheSame &&
            allPercentageAdjustmentsAreWholeIntegers
        ) {
            return `Subscribe and Save ${discountedValues[0]}%`
        }
    }

    return 'Subscribe and Save'
}

export function isPercentageAdjustment(
    adjustmentValue: SellingPlanPriceAdjustment,
) {
    return 'percentage' in adjustmentValue
}

export function isFixedAmountAdjustment(
    adjustmentValue: SellingPlanPriceAdjustment,
) {
    return 'amount' in adjustmentValue
}

export function isFixedPriceAdjustment(
    adjustmentValue: SellingPlanPriceAdjustment,
) {
    return 'price' in adjustmentValue
}

export function getDefaultSubscriptionOption(
    cartItem: CartItem,
    subscriptionOptions?: SubscriptionSetting,
) {
    return getValidSellingPlanAllocations(cartItem, subscriptionOptions)?.[0]
}

export function getSellingPlanCompareAtPrice(
    cartItem: CartItem,
    subscriptionOptions?: SubscriptionSetting,
) {
    const sellingPlanAllocation = getCurrentSubscriptionOption(
        cartItem,
        subscriptionOptions,
    )
    if (!sellingPlanAllocation) {
        return
    }
    // Guard against empty or missing priceAdjustments array
    const firstPriceAdjustment = sellingPlanAllocation.priceAdjustments?.[0]
    if (!firstPriceAdjustment) {
        return
    }
    // First price adjustment is the compare at price
    return firstPriceAdjustment.compareAtPrice
}

export function cartHasActiveSubscriptions(
    cart: Cart,
    subscriptionOptions?: SubscriptionSetting,
) {
    return cart.items.some((item) =>
        getCurrentSubscriptionOption(item, subscriptionOptions),
    )
}

export const forTesting = {
    getSubscriptionSellingPlanAllocations,
}
